import { ReactElement } from 'react';
import { Sequence, useCurrentFrame, useVideoConfig } from 'remotion';

// Types
import { CheckerboardConfig } from './types';

// Constants
import {
  DEFAULT_CONFIG,
  GRADIENT_PATTERN,
  PATTERN_COLS,
  PATTERN_ROWS,
} from './constants';

interface ScrollingTileBackgroundProps {
  /** Optional configuration overrides */
  config?: Partial<CheckerboardConfig>;
}

/**
 * ScrollingTileBackground component
 * Renders an infinite scrolling pattern with rounded tiles
 * that moves continuously to the left with slow rotation
 */
export const ScrollingTileBackground = ({
  config: configProp,
}: ScrollingTileBackgroundProps): ReactElement => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Merge config with defaults
  const config: CheckerboardConfig = {
    ...DEFAULT_CONFIG,
    ...configProp,
  };

  const { tileSize, gap, rotation, rotationSpeed, speed, backgroundColor } =
    config;

  // Calculate animated rotation based on frame and fps
  // rotationSpeed is in degrees per second
  const animatedRotation = rotation + (frame / fps) * rotationSpeed;

  // Calculate tile dimensions including gap
  const tileTotalSize = tileSize + gap;

  // Calculate offset for seamless looping
  // Loop every 4 tiles because opacity pattern has period 4 (columns)
  const loopSize = tileTotalSize * PATTERN_COLS;
  const xOffset = (frame * speed) % loopSize;

  // Calculate how many tiles we need to cover the screen plus extra for rotation and scrolling
  // Adding extra tiles to ensure coverage during rotation and animation
  const extraTiles = 10;
  const tilesX = Math.ceil(1920 / tileTotalSize) + extraTiles;
  const tilesY = Math.ceil(1080 / tileTotalSize) + extraTiles;

  // Generate tiles for the pattern
  const tiles: ReactElement[] = [];

  for (let row = -extraTiles / 2; row < tilesY; row++) {
    for (let col = -extraTiles / 2; col < tilesX + extraTiles; col++) {
      // Calculate position in the repeating pattern (4 cols x 2 rows)
      // Handle negative numbers correctly with modulo
      const patternRow = ((row % PATTERN_ROWS) + PATTERN_ROWS) % PATTERN_ROWS;
      const patternCol = ((col % PATTERN_COLS) + PATTERN_COLS) % PATTERN_COLS;

      // Get gradient from the pattern
      const gradient =
        GRADIENT_PATTERN[patternRow]?.[patternCol] ??
        GRADIENT_PATTERN[0]?.[0] ??
        '';

      const x = col * tileTotalSize - xOffset;
      const y = row * tileTotalSize;

      tiles.push(
        <div
          key={`tile-${row}-${col}`}
          className="absolute"
          style={{
            left: x,
            top: y,
            width: tileSize,
            height: tileSize,
            background: gradient,
          }}
        />,
      );
    }
  }

  return (
    <Sequence
      name="ScrollingTileBackground"
      className="overflow-hidden"
      style={{ backgroundColor }}
    >
      <div
        className="absolute h-[200%] w-[200%] -left-1/2 -top-[30%] origin-center"
        style={{
          transform: `translateZ(0) rotate(${animatedRotation}deg)`,
          willChange: 'transform',
        }}
      >
        {tiles}
      </div>
    </Sequence>
  );
};
